import { AccessibleSheet } from "@/components/common/AccessibleSheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { IntegrationFormState, IntegrationType } from "../types";
import { integrationTypes, buildCredentialValue, buildPayload, validateFormData } from "../utils";
import TypeSelector from "./modal/TypeSelector";
import TextDocumentForm from "./modal/TextDocumentForm";
import ApiForm from "./modal/ApiForm";
import McpForm from "./modal/McpForm";
import { toast } from "sonner";

interface IntegrationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedType: "text_document" | "api" | "mcp" | null;
  setSelectedType: (type: "text_document" | "api" | "mcp" | null) => void;
  formData: IntegrationFormState;
  setFormData: (data: IntegrationFormState) => void;
  onSubmit: () => void;
  isCreating: boolean;
}

const IntegrationsModal = ({
  isOpen,
  onClose,
  selectedType,
  setSelectedType,
  formData,
  setFormData,
  onSubmit,
  isCreating,
}: IntegrationsModalProps) => {
  const handleTypeSelect = (type: "api" | "mcp" | "text_document") => {
    setSelectedType(type);
    if (type === "text_document") {
      setFormData({
        access_type: "public",
        credential_type: "api_key_header",
      });
    } else if (type === "api") {
      setFormData({
        credential_type: "api_key_header",
      });
    } else {
      setFormData({});
    }
  };

  const handleSubmit = () => {
    if (!validateFormData(selectedType, formData)) {
      return;
    }

    const credentialValue = buildCredentialValue(selectedType as "text_document" | "api" | "mcp", formData);
    if (credentialValue === null) {
      return;
    }

    const payload = buildPayload(selectedType, formData, credentialValue);
    if (!payload) {
      toast.error("Invalid form data");
      return;
    }

    onSubmit();
  };

  return (
    <AccessibleSheet
      isOpen={isOpen}
      onClose={onClose}
      title={
        selectedType
          ? `${integrationTypes.find((t) => t.id === selectedType)?.label} Configuration`
          : "Select Integration Type"
      }
      className="w-full sm:max-w-xl"
    >
      <div className="flex items-start sm:items-center justify-between border-b p-4 sm:p-6 gap-4">
        <div className="flex-1">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 break-words">
            {selectedType
              ? `${integrationTypes.find((t) => t.id === selectedType)?.label} Configuration`
              : "Select Integration Type"}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl font-light flex-shrink-0"
        >
          &times;
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-4">
        {!selectedType ? (
          <TypeSelector
            integrationTypes={integrationTypes}
            onSelectType={handleTypeSelect}
          />
        ) : (
          <>
            <button
              onClick={() => setSelectedType(null)}
              className="text-xs text-gray-500 hover:text-gray-700 mb-4 block sm:hidden"
            >
              ← Back to type selection
            </button>

            {selectedType === "text_document" && (
              <TextDocumentForm
                formData={formData}
                setFormData={setFormData}
              />
            )}

            {selectedType === "api" && (
              <ApiForm
                formData={formData}
                setFormData={setFormData}
              />
            )}

            {selectedType === "mcp" && (
              <McpForm
                formData={formData}
                setFormData={setFormData}
              />
            )}
          </>
        )}
      </div>

      {selectedType && (
        <div className="sticky bottom-0 border-t px-4 sm:px-6 py-4 flex flex-col-reverse sm:flex-row justify-start gap-2 sm:gap-3 bg-white">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isCreating} className="w-full sm:w-auto">
            {isCreating ? "Creating..." : "Create Integration"}
          </Button>
        </div>
      )}
    </AccessibleSheet>
  );
};

IntegrationsModal.buildPayload = buildPayload;

export default IntegrationsModal;
